// Registry of replay error modals — mirrors Alexey's Figma "modal-error-*" set
// (page `modal`, file v1w7nEI5tGoQA1BODR782Q), rebuilt in our navy / square /
// gold style. Hero art is his pixel-art renders; until those are fetched the
// modal falls back to a per-error vector glyph (see error_illustrations/README).

import imgFileOpen from './error_illustrations/file-open.png'
import imgNetwork from './error_illustrations/network.png'
import imgVersionUnsupported from './error_illustrations/version-unsupported.png'
import imgEmptyReplay from './error_illustrations/empty-replay.png'
import imgTexturepackInvalid from './error_illustrations/texturepack-invalid.png'
import imgTexturepackNetwork from './error_illustrations/texturepack-network.png'
import imgPlayback from './error_illustrations/playback.png'
import imgRender from './error_illustrations/render.png'
import imgAuthDisabled from './error_illustrations/auth-disabled.png'
import imgUnknown from './error_illustrations/unknown.png'

export type ReplayErrorId =
  | 'file-open' | 'network' | 'version-unsupported' | 'empty-replay'
  | 'texturepack-invalid' | 'texturepack-network' | 'playback' | 'render'
  | 'auth-disabled' | 'unknown'

export type ReplayErrorActionKind = 'home' | 'retry' | 'restart' | 'openNoPack'

// Fallback glyph keys — drawn as inline SVG in the modal until the real render
// is present (see GLYPHS in ReplayErrorModal.tsx).
export type ReplayErrorGlyph =
  | 'folder' | 'plug' | 'sign' | 'empty' | 'texture' | 'download'
  | 'playback' | 'render' | 'lock' | 'unknown'

export type ReplayErrorTone = 'critical' | 'warning' | 'network' | 'info'

export interface ReplayErrorDef {
  title: string
  /** Description template. {version} / {maxVersion} are interpolated by the modal. */
  description: string
  illustration: string
  glyph: ReplayErrorGlyph
  /** Semantic light used by the report header and the icon's shadow plate. */
  tone: ReplayErrorTone
  /** Compact subsystem/status label in the report header. */
  reportLabel: string
  /** Buttons, in display order (left → right). */
  actions: ReplayErrorActionKind[]
  /** When set, show a copyable diagnostic-code chip; the prefix seeds the code. */
  codePrefix?: string
}

// Action presentation. Gold-filled = primary CTA (restart / open-without-pack),
// steel-minor = secondary (home / retry); retry & restart carry a ↻ glyph.
export const REPLAY_ERROR_ACTIONS: Record<ReplayErrorActionKind, {
  label: string
  primary: boolean
  refreshIcon: boolean
}> = {
  home: { label: 'На главную', primary: false, refreshIcon: false },
  retry: { label: 'Повторить', primary: false, refreshIcon: true },
  restart: { label: 'Начать сначала', primary: true, refreshIcon: true },
  openNoPack: { label: 'Открыть без ресурспака', primary: true, refreshIcon: false },
}

export const REPLAY_ERRORS: Record<ReplayErrorId, ReplayErrorDef> = {
  'file-open': {
    title: 'Не удалось открыть запись',
    description: 'Файл записи недоступен или повреждён. Обновите страницу или попробуйте снова через некоторое время.',
    illustration: imgFileOpen,
    glyph: 'folder',
    tone: 'warning',
    reportLabel: 'ФАЙЛ / ОТКАЗ',
    actions: ['home', 'retry'],
  },
  network: {
    title: 'Ошибка подключения',
    description: 'Проверьте подключение к интернету. Если включён VPN, отключите его и попробуйте снова.',
    illustration: imgNetwork,
    glyph: 'plug',
    tone: 'network',
    reportLabel: 'СЕТЬ / НЕДОСТУПНА',
    actions: ['retry'],
  },
  'version-unsupported': {
    title: 'Версия игры пока не поддерживается',
    description: 'Запись сделана на Minecraft {version}, а плеер умеет открывать записи до версии {maxVersion}. Поддержку новых версий уже в процессе добавления.',
    illustration: imgVersionUnsupported,
    glyph: 'sign',
    tone: 'info',
    reportLabel: 'СОВМЕСТИМОСТЬ',
    actions: ['home'],
  },
  'empty-replay': {
    title: 'В записи нет игровых данных',
    description: 'Похоже, запись прервалась ещё до того, как игрок зашёл в мир. Смотреть тут нечего.',
    illustration: imgEmptyReplay,
    glyph: 'empty',
    tone: 'info',
    reportLabel: 'ДАННЫЕ / ПУСТО',
    actions: ['home'],
  },
  'texturepack-invalid': {
    title: 'Ресурс-пак не подошёл',
    description: 'Файл текстур повреждён или это не ресурс-пак Minecraft. Можно открыть запись со стандартными текстурами.',
    illustration: imgTexturepackInvalid,
    glyph: 'texture',
    tone: 'warning',
    reportLabel: 'РЕСУРС-ПАК / ФОРМАТ',
    actions: ['retry', 'openNoPack'],
  },
  'texturepack-network': {
    title: 'Ресурс-пак не загрузился',
    description: 'Не удалось загрузить ресурс-пак.',
    illustration: imgTexturepackNetwork,
    glyph: 'download',
    tone: 'network',
    reportLabel: 'РЕСУРС-ПАК / СЕТЬ',
    actions: ['retry', 'openNoPack'],
  },
  playback: {
    title: 'Не получилось воспроизвести запись',
    description: 'Что-то пошло не так во время проигрывания. Возможно, запись содержит данные, которые плеер пока не умеет обрабатывать.',
    illustration: imgPlayback,
    glyph: 'playback',
    tone: 'critical',
    reportLabel: 'ПЛЕЕР / СБОЙ',
    actions: ['home', 'restart'],
    codePrefix: 'PLB',
  },
  render: {
    title: 'Ошибка рендеринга',
    description: 'У вашего браузера не получилось отрисовать мир. Попробуйте обновить страницу или открыть запись в Chrome либо Firefox последних версий.',
    illustration: imgRender,
    glyph: 'render',
    tone: 'critical',
    reportLabel: 'РЕНДЕР / СБОЙ',
    actions: ['home'],
  },
  'auth-disabled': {
    title: 'Авторизация недоступна',
    description: 'Попробуйте позже.',
    illustration: imgAuthDisabled,
    glyph: 'lock',
    tone: 'warning',
    reportLabel: 'ДОСТУП / ОГРАНИЧЕН',
    actions: ['home'],
  },
  unknown: {
    title: 'Что-то сломалось',
    description: 'Произошла неожиданная ошибка. Обновите страницу — обычно помогает. Если ошибка повторится, пришлите её код в поддержку.',
    illustration: imgUnknown,
    glyph: 'unknown',
    tone: 'critical',
    reportLabel: 'СИСТЕМА / НЕИЗВЕСТНО',
    actions: ['home', 'restart'],
    codePrefix: 'UN',
  },
}

export const REPLAY_ERROR_IDS = Object.keys(REPLAY_ERRORS)
